import React, { useState } from 'react'
import { Spinner } from '@fluentui/react'
import { errorToString, SolarMonitoringProvider } from '@oneethos/shared'
import { toast } from 'react-toastify'
import api from '../api-client'
import { Section } from '../components'
import { useAppState } from '../hooks'

const providerLabels: Record<SolarMonitoringProvider, string> = {
  [SolarMonitoringProvider.Enphase]: 'Enphase',
  [SolarMonitoringProvider.SolarEdge]: 'SolarEdge',
}

type ProviderConnectButtonProps = {
  provider: SolarMonitoringProvider
  workingProvider: SolarMonitoringProvider | null
  onConnect: (provider: SolarMonitoringProvider) => void
}

const ProviderConnectButton = ({
  provider,
  workingProvider,
  onConnect,
}: ProviderConnectButtonProps) => (
  <button
    className="btn btn-primary me-2 mb-2"
    disabled={workingProvider !== null}
    onClick={() => onConnect(provider)}
    type="button"
  >
    {workingProvider === provider ? <Spinner /> : `Connect ${providerLabels[provider]}`}
  </button>
)

export const ConnectMonitoringPage = () => {
  const { tenant } = useAppState()
  const [workingProvider, setWorkingProvider] = useState<SolarMonitoringProvider | null>(null)
  const tenantName = tenant?.config?.name || 'OneEthos'

  const connectProvider = async (provider: SolarMonitoringProvider): Promise<void> => {
    setWorkingProvider(provider)
    try {
      const response = await api.post(
        `/solar-monitoring/owner/authorize/${provider}`,
        {},
      )
      window.open(response.authorizationUrl, '_blank', 'noopener,noreferrer')
      toast.info(`Complete the ${providerLabels[provider]} authorization in the new window.`)
    } catch (error) {
      toast.error(errorToString(error))
    } finally {
      setWorkingProvider(null)
    }
  }

  return (
    <Section>
      <h3>Connect your solar monitoring</h3>
      <p>
        Authorize {tenantName} to view production data from your Enphase or SolarEdge system.
        You do not need an account with us to complete this step.
      </p>
      <div className="mt-3">
        {Object.values(SolarMonitoringProvider).map(provider => (
          <ProviderConnectButton
            key={provider}
            provider={provider}
            workingProvider={workingProvider}
            onConnect={connectProvider}
          />
        ))}
      </div>
    </Section>
  )
}

export const ConnectMonitoringDonePage = () => (
  <Section>
    <h3>Monitoring connected</h3>
    <p>
      Thank you. Your solar monitoring account is now connected.
      You may close this page.
    </p>
  </Section>
)
